import { uniqueId } from 'lodash-es';
import { GlBadge, GlButton, GlLink, GlForm, GlFormInput, GlFormTextarea, GlTokenSelector, GlTooltipDirective, GlAlert, GlSprintf } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { helpPagePath } from '~/helpers/help_page_helper';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { MAX_LENGTH_NAME, MAX_LENGTH_DESCRIPTION, MAX_LENGTH_PROMPT, VISIBILITY_LEVEL_PRIVATE, VISIBILITY_LEVEL_RESTRICTED, VISIBILITY_LEVEL_PUBLIC, AGENT_VISIBILITY_LEVEL_DESCRIPTIONS, AI_CATALOG_TYPE_AGENT, DEFAULT_THIRD_PARTY_FLOW_YML_STRING, AI_CATALOG_TYPE_THIRD_PARTY_FLOW } from 'ee/ai/catalog/constants';
import { TYPENAME_PROJECT } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { __, s__ } from '~/locale';
import glAbilitiesMixin from '~/vue_shared/mixins/gl_abilities_mixin';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import ErrorsAlert from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/errors_alert.vue?vue3';
import { AI_CATALOG_AGENTS_ROUTE, AI_CATALOG_AGENTS_SHOW_ROUTE } from '../router/constants';
import { getCancelRoute, getSubmitButtonText } from '../utils';
import { canReadMcpServer, showTypeSelectionInAgentForm } from '../permissions';
import aiCatalogBuiltInToolsQuery from '../graphql/queries/ai_catalog_built_in_tools.query.graphql';
import aiCatalogMcpToolsQuery from '../graphql/queries/ai_catalog_mcp_tools.query.graphql';
import aiCatalogMcpServersQuery from '../graphql/queries/ai_catalog_mcp_servers.query.graphql';
import AiCatalogFormButtons from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/ai_catalog_form_buttons.vue?vue3';
import FormGroup from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_group.vue?vue3';
import FormSection from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_section.vue?vue3';
import FormAgentType from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_agent_type.vue?vue3';
import FormFlowDefinition from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_flow_definition.vue?vue3';
import FormProjectDropdown from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/form_project_dropdown.vue?vue3';
import VisibilityLevelRadioGroup from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/ai/catalog/components/visibility_level_radio_group.vue?vue3';
export default {
    name: 'AiCatalogAgentForm',
    components: {
        ErrorsAlert,
        AiCatalogFormButtons,
        FormAgentType,
        FormFlowDefinition,
        FormGroup,
        FormSection,
        FormProjectDropdown,
        GlBadge,
        GlButton,
        GlLink,
        GlForm,
        GlFormInput,
        GlFormTextarea,
        GlTokenSelector,
        GlAlert,
        GlSprintf,
        VisibilityLevelRadioGroup
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glAbilitiesMixin(),
        glFeatureFlagsMixin()
    ],
    inject: {
        projectId: {
            default: null
        },
        isGlobalNamespace: {},
        instanceBetaFeaturesEnabled: {
            default: false
        }
    },
    apollo: {
        availableBuiltInTools: {
            query: aiCatalogBuiltInToolsQuery,
            update: (data)=>data.aiCatalogBuiltInTools.nodes.map((t)=>({
                        id: t.id,
                        machineName: t.title.toLowerCase().replace(/\s+/g, '_'),
                        name: t.title,
                        description: t.description,
                        source: 'builtin'
                    })),
            error (error) {
                Sentry.captureException(error);
            }
        },
        availableMcpTools: {
            query: aiCatalogMcpToolsQuery,
            update: (data)=>(data.aiCatalogMcpTools?.nodes ?? []).map((t)=>({
                        id: `mcp-${t.name}`,
                        mcpName: t.name,
                        machineName: t.name,
                        name: t.title,
                        description: t.description,
                        source: 'mcp',
                        iconUrl: (t.icons?.find((i)=>i.theme === 'light') || t.icons?.[0])?.src ?? null
                    })),
            error (error) {
                Sentry.captureException(error);
            }
        },
        availableMcpServers: {
            query: aiCatalogMcpServersQuery,
            variables: {
                first: 100
            },
            skip () {
                return !this.glAbilities?.readAiCatalogMcpServer;
            },
            update: (data)=>data.aiCatalogMcpServers.nodes.map((s)=>({
                        id: s.id,
                        name: s.name,
                        description: s.description
                    })),
            error (error) {
                Sentry.captureException(error);
            }
        }
    },
    props: {
        mode: {
            type: String,
            required: true,
            validator: (mode)=>[
                    'edit',
                    'create'
                ].includes(mode)
        },
        isLoading: {
            type: Boolean,
            required: true
        },
        errors: {
            type: Array,
            required: true
        },
        initialValues: {
            type: Object,
            required: false,
            default () {
                return {
                    projectId: null,
                    name: '',
                    description: '',
                    systemPrompt: '',
                    tools: [],
                    mcpTools: [],
                    mcpServers: [],
                    definition: DEFAULT_THIRD_PARTY_FLOW_YML_STRING,
                    itemType: AI_CATALOG_TYPE_AGENT,
                    visibility: VISIBILITY_LEVEL_PRIVATE
                };
            }
        }
    },
    emits: [
        'dismiss-errors',
        'select-item-type',
        'submit'
    ],
    data () {
        return {
            availableBuiltInTools: [],
            availableMcpTools: [],
            availableMcpServers: [],
            toolFilter: '',
            mcpServerFilter: '',
            formValues: {
                ...this.initialValues,
                projectId: !this.isGlobalNamespace && this.projectId ? convertToGraphQLId(TYPENAME_PROJECT, this.projectId) : this.initialValues.projectId,
                visibilityLevel: this.initialValues.visibility,
                mcpServers: this.initialValues.mcpServers
            },
            formErrors: []
        };
    },
    computed: {
        formId () {
            return uniqueId('ai-catalog-agent-form-');
        },
        isThirdPartyFlow () {
            return this.formValues.itemType === AI_CATALOG_TYPE_THIRD_PARTY_FLOW;
        },
        isEditMode () {
            return this.mode === 'edit';
        },
        showTypeSelection () {
            return showTypeSelectionInAgentForm({
                isEditMode: this.isEditMode,
                glAbilities: this.glAbilities,
                glFeatures: this.glFeatures
            });
        },
        mcpToolsEnabled () {
            return this.instanceBetaFeaturesEnabled;
        },
        preferMcpToolsEnabled () {
            return Boolean(this.glFeatures.duoAgenticChatPreferMcpTools);
        },
        canReadMcpServers () {
            return canReadMcpServer({
                glAbilities: this.glAbilities
            });
        },
        allErrors () {
            return [
                ...this.errors,
                ...this.formErrors
            ];
        },
        submitButtonText () {
            return getSubmitButtonText(this.isEditMode, s__('AICatalog|Create agent'));
        },
        cancelRoute () {
            return getCancelRoute(this.$route.params, AI_CATALOG_AGENTS_SHOW_ROUTE, AI_CATALOG_AGENTS_ROUTE);
        },
        availableTools () {
            const mcpToolsWithState = this.availableMcpTools.map((t)=>({
                    ...t,
                    disabled: !this.mcpToolsEnabled
                }));
            // Dedup mirrors AI Gateway's prefer_mcp_tools behavior: when a tool exists in
            // both DAP and the GitLab MCP server, the MCP entry wins. Saved DAP selections
            // are preserved so save/edit roundtrips never silently drop a token.
            // source: 'mcp' is kept (not 'gitlab_mcp') because availableMcpTools today only
            // contains GitLab-internal MCP tools. Revisit when third-party MCP server tools
            // surface here.
            if (this.preferMcpToolsEnabled) {
                const mcpNames = new Set(mcpToolsWithState.map((t)=>t.machineName));
                const selectedDapNames = new Set(this.formValues.tools);
                const dapKept = this.availableBuiltInTools.filter((t)=>!mcpNames.has(t.machineName) || selectedDapNames.has(t.id));
                return [
                    ...mcpToolsWithState,
                    ...dapKept
                ].sort((a, b)=>a.name.localeCompare(b.name));
            }
            return [
                ...this.availableBuiltInTools,
                ...mcpToolsWithState
            ].sort((a, b)=>a.name.localeCompare(b.name));
        },
        filteredAvailableTools () {
            return this.availableTools.filter((tool)=>tool.name.toLowerCase().includes(this.toolFilter.toLowerCase()));
        },
        selectedTools () {
            const selectedBuiltIn = this.availableTools.filter((tool)=>tool.source === 'builtin' && this.formValues.tools.includes(tool.id));
            const availableMcpNames = new Set(this.availableMcpTools.map((t)=>t.machineName));
            const selectedMcp = this.availableTools.filter((tool)=>tool.source === 'mcp' && this.formValues.mcpTools.includes(tool.mcpName));
            // Create placeholder tokens for saved MCP tools not in the available list
            // (e.g., when the feature flag is off and the query was skipped)
            const savedMcpPlaceholders = this.formValues.mcpTools.filter((name)=>!availableMcpNames.has(name)).map((name)=>({
                    id: `mcp-${name}`,
                    mcpName: name,
                    machineName: name,
                    name,
                    description: '',
                    source: 'mcp',
                    disabled: true
                }));
            // gl-token-selector forwards token.style onto its rendered gl-token, so we can
            // tint MCP tokens here instead of overriding via scoped CSS.
            return [
                ...selectedBuiltIn,
                ...selectedMcp,
                ...savedMcpPlaceholders
            ].map((tool)=>({
                    ...tool,
                    style: tool.source === 'mcp' && !tool.disabled ? {
                        borderColor: 'var(--gl-feedback-warning-border-color)',
                        backgroundColor: 'var(--gl-feedback-warning-background-color)'
                    } : null
                }));
        },
        filteredAvailableMcpServers () {
            return this.availableMcpServers.filter((server)=>server.name.toLowerCase().includes(this.mcpServerFilter.toLowerCase()));
        },
        selectedMcpServers () {
            return this.availableMcpServers.filter((server)=>this.formValues.mcpServers.includes(server.id));
        }
    },
    watch: {
        'formValues.projectId': function validateProjectField() {
            this.$nextTick(()=>{
                this.$refs.fieldProject?.validate();
            });
        },
        'formValues.itemType': {
            handler (newItemType) {
                this.$emit('select-item-type', newItemType);
            }
        }
    },
    methods: {
        handleSubmit () {
            const isFormValid = this.validate();
            if (!isFormValid) {
                return;
            }
            const transformedValues = {
                projectId: this.isEditMode ? undefined : this.formValues.projectId,
                name: this.formValues.name.trim(),
                description: this.formValues.description.trim(),
                itemType: this.formValues.itemType,
                visibility: this.formValues.visibilityLevel
            };
            if (this.isThirdPartyFlow) {
                transformedValues.definition = this.formValues.definition.trim();
            } else {
                transformedValues.systemPrompt = this.formValues.systemPrompt.trim();
                transformedValues.tools = this.formValues.tools;
                transformedValues.mcpTools = this.formValues.mcpTools;
                transformedValues.mcpServers = this.formValues.mcpServers;
            }
            this.$emit('submit', transformedValues);
        },
        handleToolsInput (input) {
            this.formValues.tools = input.filter((t)=>t.source === 'builtin' && !t.disabled).map((t)=>t.id);
            this.formValues.mcpTools = input.filter((t)=>t.source === 'mcp').map((t)=>t.mcpName);
        },
        handleToolSearch (search) {
            this.toolFilter = search;
        },
        handleMcpServersInput (input) {
            this.formValues.mcpServers = input.map((s)=>s.id);
        },
        handleMcpServerSearch (search) {
            this.mcpServerFilter = search;
        },
        onError (error) {
            this.formErrors.push(error);
        },
        dismissErrors () {
            this.formErrors = [];
            this.$emit('dismiss-errors');
        },
        validate () {
            return Object.keys(this.$refs).filter((key)=>key.startsWith('field')).reduce((allValid, key)=>{
                const field = this.$refs[key];
                if (!field) return allValid;
                const isFieldValid = this.$refs[key].validate();
                return allValid && isFieldValid;
            }, true);
        }
    },
    visibilityLevelTexts: {
        textPrivate: AGENT_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_PRIVATE],
        textRestricted: AGENT_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_RESTRICTED],
        textPublic: AGENT_VISIBILITY_LEVEL_DESCRIPTIONS[VISIBILITY_LEVEL_PUBLIC]
    },
    fields: {
        projectId: {
            id: 'agent-form-project-id',
            label: s__('AICatalog|Managed by'),
            validations: {
                requiredLabel: s__('AICatalog|Project is required.')
            },
            groupAttrs: {
                labelDescription: s__('AICatalog|Only members of this project can edit or delete this agent.')
            }
        },
        name: {
            id: 'agent-form-name',
            label: __('Display name'),
            validations: {
                requiredLabel: s__('AICatalog|Name is required.'),
                maxLength: MAX_LENGTH_NAME
            },
            inputAttrs: {
                'data-testid': 'agent-form-input-name',
                placeholder: s__('AICatalog|Research Assistant, Creative Writer, Code Helper')
            }
        },
        description: {
            id: 'agent-form-description',
            label: __('Description'),
            validations: {
                requiredLabel: s__('AICatalog|Description is required.'),
                maxLength: MAX_LENGTH_DESCRIPTION
            }
        },
        itemType: {
            id: 'agent-form-type',
            label: __('Type'),
            groupAttrs: {
                labelDescription: s__('AICatalog|Build a custom agent in GitLab or connect to an AI model provider you already use.')
            }
        },
        systemPrompt: {
            id: 'agent-form-system-prompt',
            label: s__('AICatalog|System prompt'),
            validations: {
                requiredLabel: s__('AICatalog|System prompt is required.'),
                maxLength: MAX_LENGTH_PROMPT
            },
            groupAttrs: {
                labelDescription: s__("AICatalog|Define the agent's personality, expertise, and behavior. This changes how the agent solves problems and responds to requests.")
            }
        },
        visibilityLevel: {
            id: 'agent-form-visibility-level',
            label: __('Visibility'),
            groupAttrs: {
                labelDescription: s__('AICatalog|Choose who can view and interact with this agent.')
            }
        },
        tools: {
            id: 'agent-form-tools',
            label: s__('AICatalog|Tools'),
            groupAttrs: {
                optional: true
            }
        },
        mcpServers: {
            id: 'agent-form-mcp-servers',
            label: s__('AICatalog|MCP servers'),
            groupAttrs: {
                optional: true,
                labelDescription: s__('AICatalog|Connect Model Context Protocol servers to extend this agent with external tools and data sources.')
            }
        },
        definition: {
            id: 'agent-form-configuration',
            label: s__('AICatalog|YAML configuration'),
            validations: {
                requiredLabel: s__('AICatalog|Configuration is required.')
            },
            groupAttrs: {
                labelDescription: s__('AICatalog|This YAML configuration file determines the prompts, tools, and capabilities of your flow. Required properties: injectGatewayToken, image, commands')
            }
        }
    },
    AI_CATALOG_TYPE_AGENT,
    VISIBILITY_LEVEL_PUBLIC,
    toolsDocsLink: helpPagePath('user/duo_agent_platform/agents/tools'),
    publicDeletionHelpLink: helpPagePath('user/duo_agent_platform/agents/custom', {
        anchor: 'agent-visibility'
    })
};
